import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, ChevronDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import axios from "axios";

const API = "/api";

/* Separate text from <div class="visual"> HTML blocks */
function parseInsightContent(text) {
  if (!text) return { textContent: "", visualHtml: "" };
  let cleaned = text;
  cleaned = cleaned.replace(/```html\s*\n?([\s\S]*?)```/gi, "$1");
  cleaned = cleaned.replace(/```\s*\n?([\s\S]*?)```/gi, "$1");
  const match = cleaned.match(/<div\s+class=["']visual["'][\s\S]*$/i);
  if (match) {
    const visualHtml = match[0];
    const textContent = cleaned.slice(0, match.index).trim();
    return { textContent, visualHtml };
  }
  return { textContent: cleaned, visualHtml: "" };
}

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.max(1, Math.ceil(value / 30));
    const timer = setInterval(() => {
      start += step;
      if (start >= value) { setDisplay(value); clearInterval(timer); }
      else setDisplay(start);
    }, 30);
    return () => clearInterval(timer);
  }, [value]);
  return <span>{display}</span>;
}

function ThemeBar({ label, count, max, delay }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth((count / max) * 100), delay);
    return () => clearTimeout(t);
  }, [count, max, delay]);

  const isSmall = width < 15;

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm font-sans text-[#4B5563] w-44 text-right shrink-0">{label}</span>
      <div className="flex-1 flex items-center gap-2">
        <div className="flex-1 h-8 bg-[#FBF7F0] rounded-full overflow-hidden">
          <div className="h-full bg-[#E91E8C] rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-3"
            style={{ width: `${Math.max(width, 6)}%` }}>
            {!isSmall && <span className="text-xs font-sans font-bold text-white">{count}</span>}
          </div>
        </div>
        {isSmall && <span className="text-xs font-sans font-bold text-[#E91E8C] shrink-0">{count}</span>}
      </div>
    </div>
  );
}

/* Extract a preview snippet from the markdown answer, stripping the title */
function getPreviewText(markdown, title = "", maxLen = 160) {
  if (!markdown) return "";
  // Strip markdown formatting for preview
  let plain = markdown
    // Remove first heading line entirely (# Title or ## Title)
    .replace(/^#{1,3}\s+.+\n*/m, "")
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, "$1")
    .replace(/[_~`]/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[-*]\s+/g, "")
    .replace(/\n+/g, " ")
    .trim();
  // Strip the title from the beginning of the preview text
  if (title && plain.startsWith(title)) {
    plain = plain.slice(title.length).trim();
  }
  return plain.length > maxLen ? plain.slice(0, maxLen) + "..." : plain;
}

/* Expandable Insight Card — accordion style */
function InsightCard({ insight, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [open, insight.answer]);

  const date = insight.published_at
    ? new Date(insight.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "";

  const { textContent } = parseInsightContent(insight.answer);
  const insightTitle = insight.title || insight.prompt;
  const preview = getPreviewText(textContent, insightTitle);

  return (
    <div data-testid={`insight-card-${insight.id}`} className="relative group">
      {/* Amber accent line */}
      <div className="absolute top-0 left-8 w-12 h-1 bg-[#E91E8C] rounded-full z-10" />

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden transition-shadow duration-300 hover:shadow-md">
        {/* Clickable Header */}
        <button
          data-testid={`insight-toggle-${insight.id}`}
          onClick={() => setOpen(!open)}
          className="w-full text-left bg-[#16130F] px-8 md:px-10 py-6 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E91E8C] focus-visible:ring-inset"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.25em] font-sans text-[#E91E8C] mb-2">
                What we noticed
              </p>
              <h3 className="text-lg md:text-xl font-medium text-white leading-snug" style={{ fontFamily: "var(--genz-font-display)" }}>
                {insightTitle}
              </h3>
              <p className="text-xs text-white/50 mt-3 font-sans">
                {date} &middot; drawn from every why shared
              </p>
              <p className="text-xs mt-1.5" style={{ color: '#E91E8C', opacity: 0.7 }}>
                Analysed by Mira
              </p>
            </div>
            <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 ${open ? "rotate-180" : ""}`}>
              <ChevronDown size={18} className="text-white/70" />
            </div>
          </div>

          {/* Preview text when collapsed */}
          {!open && preview && (
            <p className="mt-4 text-sm font-sans text-white/40 line-clamp-2 leading-relaxed">
              {preview}
            </p>
          )}
        </button>

        {/* Expandable Content */}
        <div
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{ maxHeight: open ? `${height}px` : "0px" }}
        >
          <div ref={contentRef}>
            {(() => {
              const { textContent, visualHtml } = parseInsightContent(insight.answer);
              return (
                <>
                  {textContent && (
                    <div className="px-8 md:px-10 py-8 insight-markdown">
                      <ReactMarkdown
                        components={{
                          h1: ({ children }) => (
                            <h2 className="text-xl font-semibold text-[#16130F] mt-8 mb-3 first:mt-0">{children}</h2>
                          ),
                          h2: ({ children }) => (
                            <h3 className="text-lg font-semibold text-[#E91E8C] mt-8 mb-3 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#E91E8C] flex-shrink-0" />
                              {children}
                            </h3>
                          ),
                          h3: ({ children }) => (
                            <h4 className="text-base font-semibold text-[#16130F] mt-6 mb-2">{children}</h4>
                          ),
                          p: ({ children }) => (
                            <p className="font-sans text-base leading-relaxed text-[#4B5563] mb-4">{children}</p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-sans font-semibold text-[#16130F]">{children}</strong>
                          ),
                          em: ({ children }) => (
                            <em className="text-[#E91E8C] not-italic font-sans">{children}</em>
                          ),
                          ul: ({ children }) => (
                            <ul className="space-y-2 my-4 ml-1">{children}</ul>
                          ),
                          li: ({ children }) => (
                            <li className="flex gap-3 text-base leading-relaxed">
                              <span className="text-[#E91E8C] mt-2 flex-shrink-0">&#x2022;</span>
                              <span className="font-sans text-[#4B5563]">{children}</span>
                            </li>
                          ),
                          hr: () => (
                            <div className="my-8 flex items-center gap-3">
                              <div className="flex-1 h-px bg-[#E5E7EB]" />
                              <div className="w-1.5 h-1.5 rounded-full bg-[#E91E8C]" />
                              <div className="flex-1 h-px bg-[#E5E7EB]" />
                            </div>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-3 border-[#E91E8C] pl-5 my-6 bg-[#FBF7F0] py-4 pr-5 rounded-r-xl">
                              {children}
                            </blockquote>
                          ),
                        }}
                      >
                        {textContent}
                      </ReactMarkdown>
                    </div>
                  )}
                  {visualHtml && (
                    <div className="border-t border-[#E5E7EB] px-8 md:px-10 py-8 bg-[#FAEEDA]/30">
                      <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#E91E8C] mb-4">Visual</p>
                      <div
                        data-testid={`insight-visual-${insight.id}`}
                        className="insight-visual-container"
                        dangerouslySetInnerHTML={{ __html: visualHtml }}
                      />
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const [data, setData] = useState(null);
  const [published, setPublished] = useState([]);
  const [seekingData, setSeekingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/insights/public`),
      axios.get(`${API}/insights/published`),
      axios.get(`${API}/stories/seeking`),
    ]).then(([insightsRes, publishedRes, seekingRes]) => {
      setData(insightsRes.data);
      setPublished(publishedRes.data);
      setSeekingData(seekingRes.data);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-[#E91E8C]" />
      </div>
    );
  }

  if (!data) return null;

  const maxTheme = Math.max(...(data.themes.map(t => t.count) || [1]), 1);

  return (
    <div data-testid="insights-page" className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-[#FBF7F0]" style={{ padding: '48px 0 40px' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold text-[#E91E8C] mb-4">
            Live data
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#16130F]">
            What we're learning
          </h1>
          <p className="mt-4 max-w-2xl mx-auto" style={{ fontFamily: "var(--genz-font-display)", fontSize: '20px', lineHeight: '1.9', color: '#4B5563', fontStyle: 'italic' }}>
            We asked people one question: why do you do what you do? Here is what their answers are teaching us.
          </p>
          <p className="mt-6 max-w-xl mx-auto" style={{ fontFamily: "var(--genz-font-display)", fontSize: '17px', lineHeight: '1.9', color: '#E91E8C', fontStyle: 'italic' }}>
            every new story changes what we know.
          </p>
        </div>
      </section>

      {/* Themes */}
      {data.themes.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold text-[#E91E8C] mb-3">
              Motivation patterns
            </p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#16130F] mb-3">
              Why people do what they do
            </h2>
            <p className="font-sans text-base md:text-lg text-[#4B5563] mb-10 max-w-xl">
              Every story carries a reason. When we look across all of them, patterns emerge.
            </p>
            <div className="space-y-3">
              {data.themes.map((t, i) => (
                <ThemeBar key={t.theme} label={t.label} count={t.count} max={maxTheme} delay={i * 100} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Words */}
      {data.top_words.length > 0 && (
        <section className="bg-[#16130F] py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold text-[#E91E8C] mb-3">
              The language of purpose
            </p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-10">
              The words that keep appearing
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {data.top_words.map((w) => {
                const scale = Math.max(0.7, Math.min(1.8, w.count / 4));
                const opacity = Math.max(0.35, Math.min(1, w.count / 6));
                return (
                  <span key={w.word} className="font-sans text-[#E91E8C] transition-all duration-300 hover:opacity-100"
                    style={{ fontSize: `${scale}rem`, opacity }}>
                    {w.word}
                  </span>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Published Insights — expandable accordion cards */}
      {published.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold text-[#E91E8C] mb-3">
              What we're discovering
            </p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#16130F] mb-4">
              Insights from the stories
            </h2>
            <p className="font-sans text-base text-[#4B5563] mb-10 max-w-xl">
              Click any finding to expand the full analysis.
            </p>
            <div className="space-y-5">
              {published.map((insight, i) => (
                <InsightCard key={insight.id} insight={insight} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* People */}
      <section className="py-16 md:py-24 bg-[#FBF7F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {data.roles.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold text-[#E91E8C] mb-3">
                  Who they are
                </p>
                <h3 className="text-xl font-medium text-[#16130F] mb-6">The humans sharing</h3>
                <div className="space-y-2">
                  {data.roles.map(r => (
                    <div key={r.role} className="flex items-center justify-between py-2 border-b border-[#E5E7EB] last:border-0">
                      <span className="font-sans text-base text-[#16130F]">{r.role}</span>
                      <span className="text-xs font-sans font-semibold text-[#E91E8C]">{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.cities.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold text-[#E91E8C] mb-3">
                  Where they are
                </p>
                <h3 className="text-xl font-medium text-[#16130F] mb-6">Cities represented</h3>
                <div className="space-y-2">
                  {data.cities.map(c => (
                    <div key={c.city} className="flex items-center justify-between py-2 border-b border-[#E5E7EB] last:border-0">
                      <span className="font-sans text-base text-[#16130F]">{c.city}</span>
                      <span className="text-xs font-sans font-semibold text-[#E91E8C]">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* The Ones Still Searching */}
      <section data-testid="seeking-section" className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="rounded-2xl overflow-hidden" style={{ background: '#2C2C2A' }}>
            <div className="px-8 md:px-12 py-10 md:py-14">
              <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold mb-4" style={{ color: '#E91E8C' }}>
                The ones still searching
              </p>
              <p className="font-sans text-base md:text-lg leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Not everyone who comes to iambecause has found their why yet. Some are still in the middle of it &mdash; restless, unsatisfied, looking. We asked Mira what she noticed about the searching stories.
              </p>
              <p className="font-sans text-sm mb-2" style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                She said this:
              </p>
              <blockquote className="border-l-2 pl-6 my-6" style={{ borderColor: '#E91E8C' }}>
                <p className="font-sans text-base md:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)', fontStyle: 'italic', lineHeight: '1.9' }}>
                  &ldquo;The people who say they haven&rsquo;t found their why yet are often the most awake. They haven&rsquo;t settled. They haven&rsquo;t stopped asking. That refusal &mdash; to accept a life that doesn&rsquo;t feel entirely true &mdash; is itself a kind of answer. The searching is not the absence of purpose. It is purpose, still becoming.&rdquo;
                </p>
              </blockquote>
              <p className="text-xs font-sans mt-8" style={{ color: '#E91E8C' }}>
                Observed by Mira
              </p>

              {/* Dynamic seeking stories content */}
              {seekingData && seekingData.count > 0 && (
                <div className="mt-8 pt-6" style={{ borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-sans text-sm italic mb-5" style={{ color: '#E91E8C' }}>
                    the ones still searching have shared here too. they belong just as much.
                  </p>
                  {seekingData.random_story && (
                    <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
                      <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        One searcher's words
                      </p>
                      <p className="font-sans text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
                        &ldquo;{seekingData.random_story.why}&rdquo;
                      </p>
                      <p className="text-xs font-sans mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
                        — {seekingData.random_story.name}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* CTA — Talk to Mira */}
              <div className="mt-8 pt-6" style={{ borderTop: seekingData && seekingData.count > 0 ? 'none' : '0.5px solid rgba(255,255,255,0.1)' }}>
                <button
                  data-testid="seeking-talk-to-mira"
                  onClick={() => window.dispatchEvent(new CustomEvent('openMiraSeeking'))}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-sans font-medium transition-all hover:scale-105"
                  style={{ border: '1px solid rgba(233,30,140,0.4)', color: '#E91E8C', background: 'rgba(233,30,140,0.08)' }}
                >
                  Talk to Mira &rarr;
                </button>
                <p className="mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                  Mira is the one who knows — she listens, she asks, she remembers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#16130F] mb-3">
            Add your data point.
          </h2>
          <p className="font-sans text-lg text-[#4B5563] mb-8 max-w-lg mx-auto">
            Every story changes the picture. What would yours add?
          </p>
          <button
            data-testid="insights-cta-mira"
            onClick={() => {
              const orb = document.querySelector('[data-testid="mira-orb"]');
              if (orb) orb.click();
            }}
            className="btn-amber inline-flex"
          >
            Talk to Mira <ArrowRight size={16} />
          </button>
          <p className="mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '11px', color: '#888780', fontStyle: 'italic' }}>
            Mira is the one who knows — she listens, she asks, she remembers.
          </p>
        </div>
      </section>
    </div>
  );
}
